import React, { useState, useEffect } from 'react';
import { createMarkdownFile, fetchImageFolders, fetchImagesInFolder, fetchMarkdownFilesRecursive } from '../api';

const DEFAULT_FRONTMATTER = {
  Title: '',
  description: '',
  date: new Date().toISOString(),
  Image: '',
  featureImage: '',
  newsCategories: [''],
  newsTags: [''],
  blogTags: [''],
  blogCategories: [''],
  author: '',
  draft: false,
  breakingNews: false
};

function serializeFrontmatter(data) {
  const lines = Object.entries(data).map(([key, value]) => {
    if (typeof value === 'boolean') return `${key}: ${value}`;
    if (Array.isArray(value)) return `${key}: ${JSON.stringify(value)}`;
    return `${key}: "${value}"`;
  });
  return `---\n${lines.join('\n')}\n---`;
}

const CreateFile = ({ onFileCreated }) => { 
  const [fileName, setFileName] = useState('');
  const [folder, setFolder] = useState('');
  const [frontmatter, setFrontmatter] = useState(DEFAULT_FRONTMATTER);
  const [body, setBody] = useState('');
  const [creating, setCreating] = useState(false);
  const [imageFolders, setImageFolders] = useState([]);
  const [imageOptions, setImageOptions] = useState({});
  const [selectedImageFolders, setSelectedImageFolders] = useState({});
  const [existingFolders, setExistingFolders] = useState([]);

  useEffect(() => {
    fetchImageFolders().then(setImageFolders);
    fetchMarkdownFilesRecursive('content').then(files => {
      const folders = new Set();
      files.forEach(f => {
        const path = f.path.replace(/^content\//, '');
        const parts = path.split('/');
        if (parts.length > 1) {
          folders.add(parts.slice(0, -1).join('/'));
        }
      });
      setExistingFolders(Array.from(folders));
    });
  }, []);

  const loadImages = async (folder) => {
    const images = await fetchImagesInFolder(`static/images/${folder}`);
    setImageOptions(prev => ({ ...prev, [folder]: images }));
  };

  const handleFieldChange = (key, value) => {
    setFrontmatter(prev => ({ ...prev, [key]: value }));
  };

  const handleImageFolderChange = (field, folder) => {
    setSelectedImageFolders(prev => ({ ...prev, [field]: folder }));
    loadImages(folder);
  };

  const handleCreate = async () => {
    if (!fileName.endsWith('.md')) {
      alert('Filename must end with .md');
      return;
    }
    if (!folder) {
      alert('Select or enter a folder');
      return;
    }
    setCreating(true);
    const fullPath = `content/${folder}/${fileName}`;
    const fm = serializeFrontmatter(frontmatter);
    const content = `${fm}\n\n${body}`;
    try {
      await createMarkdownFile(fullPath, content);
      alert('File created');
      
      // 2. Call the function from the parent here!
      if (onFileCreated) {
        onFileCreated();
      }

      // You can keep or remove these state resets, as the modal will close anyway
      setFileName('');
      setFolder('');
      setFrontmatter(DEFAULT_FRONTMATTER);
      setBody('');

    } catch (err) {
      alert('Error: ' + err.message);
    }
    setCreating(false);
  };

  const renderField = (key, value) => {
    if (key === 'date') {
      return (
        <input
          type="datetime-local"
          value={new Date(value).toISOString().slice(0, 16)}
          onChange={e => handleFieldChange(key, new Date(e.target.value).toISOString())}
          style={styles.input}
        />
      );
    }

    if (key === 'Image') {
      const folder = selectedImageFolders[key];
      const images = folder ? imageOptions[folder] || [] : [];
      return (
        <div>
          <select
            onChange={e => handleImageFolderChange(key, e.target.value)}
            value={folder || ''}
            style={{ ...styles.input, marginBottom: 6 }}
          >
            <option value="">Select Folder</option>
            {imageFolders.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <select
            value={value}
            onChange={e => handleFieldChange(key, e.target.value)}
            style={styles.input}
          >
            <option value="">Select Image</option>
            {images.map(img => (
              <option key={img.name} value={img.name}>
                {img.name}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (key === 'featureImage') {
      const images = imageOptions['featureImage'] || [];
      return (
        <select
          value={value}
          onClick={() => loadImages('featureImage')}
          onChange={e => handleFieldChange(key, e.target.value)}
          style={styles.input}
        >
          <option value="">Select Feature Image</option>
          {images.map(img => (
            <option key={img.name} value={img.name}>
              {img.name}
            </option>
          ))}
        </select>
      );
    }

    if (typeof value === 'boolean') {
      return (
        <input
          type="checkbox"
          checked={value}
          onChange={e => handleFieldChange(key, e.target.checked)}
          style={styles.checkbox}
        />
      );
    }

    if (Array.isArray(value)) {
      return (
        <input
          type="text"
          value={value.join(', ')}
          onChange={e => handleFieldChange(key, e.target.value.split(',').map(x => x.trim()))}
          style={styles.input}
          placeholder="Comma-separated"
        />
      );
    }

    return (
      <input
        type="text"
        value={value}
        onChange={e => handleFieldChange(key, e.target.value)}
        style={styles.input}
      />
    );
  };

  return (
    <div style={styles.container}>
      <h2>Create New File</h2>
      <div style={styles.fieldWrapper}>
        <label style={styles.label}>Folder</label>
        <select
          value={folder}
          onChange={e => setFolder(e.target.value)}
          style={styles.input}
        >
          <option value="">Select Folder</option>
          {existingFolders.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <input
          type="text"
          placeholder="Or enter new folder"
          value={folder}
          onChange={e => setFolder(e.target.value)}
          style={styles.input}
        />
      </div>
      <div style={styles.fieldWrapper}>
        <label style={styles.label}>File Name (.md)</label>
        <input
          type="text"
          value={fileName}
          onChange={e => setFileName(e.target.value)}
          placeholder="example.md"
          style={styles.input}
        />
      </div>
      <h3 style={styles.subheading}>Frontmatter</h3>
      {Object.entries(frontmatter).map(([key, value]) => (
        <div key={key} style={styles.fieldWrapper}>
          <label style={styles.label}>{key}</label>
          {renderField(key, value)}
        </div>
      ))}
      <div style={styles.fieldWrapper}>
        <label style={styles.label}>Markdown Body</label>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          rows={10}
          style={styles.textarea}
        />
      </div>
      <button onClick={handleCreate} disabled={creating} style={styles.button}>
        {creating ? 'Creating...' : 'Create File'}
      </button>
    </div>
  );
};

const styles = {
  container: {
    padding: 24,
    maxWidth: 700,
    margin: '0 auto',
    fontFamily: 'system-ui, sans-serif'
  },
  label: {
    display: 'block',
    marginBottom: 4,
    fontWeight: 'bold'
  },
  input: {
    width: '100%',
    padding: 8,
    marginBottom: 12,
    fontSize: '1rem',
    borderRadius: 4,
    border: '1px solid #ccc'
  },
  checkbox: {
    transform: 'scale(1.2)',
    marginTop: 4
  },
  fieldWrapper: {
    marginBottom: 16
  },
  textarea: {
    width: '100%',
    padding: 10,
    fontSize: '1rem',
    borderRadius: 4,
    border: '1px solid #ccc'
  },
  button: {
    padding: '10px 20px',
    fontSize: '1rem',
    background: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer'
  },
  subheading: {
    marginTop: 24,
    fontSize: '1.2rem',
    borderBottom: '1px solid #ddd',
    paddingBottom: 4
  }
};

export default CreateFile;
